import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/maplibre';
import { fillColor, TankOverlay, TankReading } from '../../data/tankGauge';

/**
 * Tank fill levels, painted over the tank polygons on the map.
 *
 * Rendered as a child of <Map> — Source/Layer only work inside the map subtree — and kept in
 * its own component so App.tsx gains a few lines rather than a hundred, the same shape as
 * GeolocatedPreview.
 *
 * This deliberately does NOT write map_polygons.color. Derived state stays out of source
 * data: a reading is an estimate with an error bar, the tank's colour is survey data from
 * import_refinery_layout.py, and re-running that importer would silently reset anything we
 * wrote there anyway. The layer sits on top and can be switched off.
 *
 * When a tank is selected, its measurement is drawn too — the fitted rim, the crescent and
 * the ground shadow, in world coordinates. That is the evidence for the number, and an
 * operator who cannot see where the measurement landed has no way to tell a good fit from a
 * confident wrong one.
 */

interface TankGaugeLayerProps {
  /** The app's existing polygons. Only those with a reading are painted. */
  polygons: Array<{ id: number; coordinates: number[][] }>;
  readings: Record<number, TankReading>;
  /** Measurement geometry for the currently open tank, if any. */
  highlight?: TankOverlay | null;
  visible: boolean;
}

export const TANK_FILL_LAYER = 'tank-gauge-fill';

export default function TankGaugeLayer({
  polygons, readings, highlight, visible,
}: TankGaugeLayerProps) {
  const fills = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: polygons.flatMap((p) => {
      const reading = readings[p.id];
      if (!reading) return [];
      // A refused tank is drawn grey rather than omitted. "We looked and could not read
      // it" and "we never looked" are different states, and the operator needs to tell
      // them apart without opening every tank.
      const fill = reading.ok ? reading.fill_fraction ?? null : null;
      return [{
        type: 'Feature' as const,
        properties: {
          polygonId: p.id,
          color: fillColor(fill),
          opacity: reading.ok ? 0.55 : 0.25,
        },
        geometry: { type: 'Polygon' as const, coordinates: [p.coordinates] },
      }];
    }),
  }), [polygons, readings]);

  const evidence = useMemo(() => {
    if (!highlight) return null;
    const features = [];
    if (highlight.shadow) {
      features.push({
        type: 'Feature' as const, properties: { kind: 'shadow' },
        geometry: { type: 'LineString' as const, coordinates: highlight.shadow },
      });
    }
    if (highlight.crescent) {
      features.push({
        type: 'Feature' as const, properties: { kind: 'crescent' },
        geometry: { type: 'LineString' as const, coordinates: highlight.crescent },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [highlight]);

  if (!visible) return null;

  return (
    <>
      <Source id="tank-gauge-fills" type="geojson" data={fills}>
        <Layer
          id={TANK_FILL_LAYER}
          type="fill"
          paint={{
            'fill-color': ['get', 'color'],
            'fill-opacity': ['get', 'opacity'],
          }}
        />
        <Layer
          id="tank-gauge-outline"
          type="line"
          paint={{ 'line-color': ['get', 'color'], 'line-width': 1.5 }}
        />
      </Source>

      {evidence && (
        <Source id="tank-gauge-evidence" type="geojson" data={evidence}>
          {/* The ground shadow: what the tank's height was measured from. */}
          <Layer
            id="tank-gauge-shadow"
            type="line"
            filter={['==', ['get', 'kind'], 'shadow']}
            paint={{ 'line-color': '#38bdf8', 'line-width': 3 }}
          />
          {/* The crescent: what the roof depth was measured from. */}
          <Layer
            id="tank-gauge-crescent"
            type="line"
            filter={['==', ['get', 'kind'], 'crescent']}
            paint={{ 'line-color': '#f472b6', 'line-width': 3 }}
          />
        </Source>
      )}
    </>
  );
}
